'use client'
import React, { useState, useEffect, useRef, useCallback } from 'react'

import { useTranslations, useLocale } from 'next-intl'
import { usePathname, useRouter } from '@/src/i18n/navigation'
import { useSearchParams } from 'next/navigation'

import { useAppSelector, useAppDispatch } from '@/src/redux/store'
import { selectAuth, refreshAuthUser } from '@/src/redux/slices/authSlice'
import { setCurrency } from '@/src/redux/slices/currencySlice'
import { navLinks, languageOptions, currencyOptions } from '@/src/constants/header'
import { useGetCurrencyDataQuery } from '@/src/api/rtkQuery/currencyApi'
import { getLanguageByCode } from '@/src/utils/language'
import { mapApiCurrencyOptions } from '@/src/utils/currency'
import { setLanguageCookie } from '@/src/utils/language'

import { Header } from './Header'
import { SignInModalWrapper } from './SignInModal'

const HeaderContainer: React.FC = () => {
    const [isMounted, setIsMounted] = useState(false)
    const [modals, setModals] = useState<{ signIn: boolean }>({ signIn: false })

    // Get current locale from URL
    const locale = useLocale()

    // Use refs for menu anchors to avoid unnecessary re-renders
    const userMenuAnchorRef = useRef<HTMLElement | null>(null)
    const languageMenuAnchorRef = useRef<HTMLElement | null>(null)
    const currencyMenuAnchorRef = useRef<HTMLElement | null>(null)

    // Track menu open states for re-rendering when needed
    const [menuStates, setMenuStates] = useState<{
        user: boolean
        language: boolean
        currency: boolean
    }>({ user: false, language: false, currency: false })

    // Initialize selected language based on current locale (using utility)
    const [selected, setSelected] = useState({
        language: getLanguageByCode(locale),
        currency: currencyOptions[0]
    })

    // Fetch currency list from API
    const { data: currencyData, isLoading: isCurrencyLoading, isError: isCurrencyError } = useGetCurrencyDataQuery()

    // Map API data to expected shape for dropdown (using utility)
    const apiCurrencyOptions = React.useMemo(() => mapApiCurrencyOptions(currencyData), [currencyData])

    const currency = useAppSelector(state => state.currency.selected)
    const dispatch = useAppDispatch()

    const t = useTranslations('header')
    const tCurrency = useTranslations('Currency')
    const user = useAppSelector(selectAuth)
    const pathname = usePathname()
    const router = useRouter()
    const searchParams = useSearchParams()



    // Define which routes should show the language/currency dropdown
    const showLangCurrDropdown = React.useMemo(() => {
        if (pathname === '/flights' || /^\/[a-z]{2}\/flights/.test(pathname)) return true
        if (pathname === '/flights/results' || /^\/[a-z]{2}\/flights\/results/.test(pathname)) return true
        if (pathname.includes('/offers')) return true // Show on offers page
        return false
    }, [pathname])


    // Check if current page is offers page
    const isOffersPage = pathname.includes('/offers')

    const isResultsPage = pathname.includes('/flights/results')

    // Check if we're on home page (strict login wall)
    // usePathname from next-intl returns path without locale
    const isHomePage = pathname === '/'

    // Check if we're on itinerary page - always hide sign-in on itinerary page
    const isItineraryPage = pathname.includes('/flights/itinerary')
    const shouldHideSignInOnItinerary = isItineraryPage

    // Redirect logged in users from home page to flights
    useEffect(() => {
        if (isHomePage && user.signedIn) {
            router.push('/flights')
        }
    }, [isHomePage, user.signedIn, router])

    useEffect(() => {
        // Only run on client-side
        if (typeof window === 'undefined') return
        dispatch(refreshAuthUser())
        setIsMounted(true)
    }, [dispatch])

    useEffect(() => {
        if (!isMounted) return

        // Update currency if needed
        if (!isCurrencyLoading && !isCurrencyError && apiCurrencyOptions.length > 0) {
            const found = apiCurrencyOptions.find((opt: any) => opt.code === currency)
            if (found) {
                setSelected(prev => ({
                    ...prev,
                    currency: found
                }))
            }
        }
    }, [isMounted, currency, apiCurrencyOptions, isCurrencyLoading, isCurrencyError])

    // Sync language selection with locale changes (using utility)
    useEffect(() => {
        const langOption = getLanguageByCode(locale)
        setSelected(prev => ({
            ...prev,
            language: langOption
        }))
    }, [locale])

    const options = {
        language: languageOptions,
        currency:
            !isCurrencyLoading && !isCurrencyError && apiCurrencyOptions.length > 0
                ? apiCurrencyOptions
                : currencyOptions // fallback to static
    }

    const onMenuOpen = useCallback((menu: 'user' | 'language' | 'currency', event: React.MouseEvent<HTMLElement>) => {
        if (menu === 'user') {
            userMenuAnchorRef.current = event.currentTarget
        } else if (menu === 'language') {
            languageMenuAnchorRef.current = event.currentTarget
        } else if (menu === 'currency') {
            currencyMenuAnchorRef.current = event.currentTarget
        }
        setMenuStates(prev => ({ ...prev, [menu]: true }))
    }, [])

    const onMenuClose = useCallback((menu: 'user' | 'language' | 'currency') => {
        if (menu === 'user') {
            userMenuAnchorRef.current = null
        } else if (menu === 'language') {
            languageMenuAnchorRef.current = null
        } else if (menu === 'currency') {
            currencyMenuAnchorRef.current = null
        }
        setMenuStates(prev => ({ ...prev, [menu]: false }))
    }, [])

    const onSelect = useCallback(
        (type: 'language' | 'currency', value: any) => {
            setSelected(prev => ({ ...prev, [type]: value }))
            if (type === 'currency') {
                dispatch(setCurrency({ code: value.code, symbol: value.symbol }))
            } else if (type === 'language') {
                // Set language cookie
                setLanguageCookie(value.code)

                // Switch locale in URL path
                const currentPath = window.location.pathname
                const currentSearch = window.location.search
                const currentHash = window.location.hash

                // Replace the locale in the path (assuming it's always /en/ or /ar/ at the start)
                let newPath = currentPath
                if (currentPath.startsWith('/en')) {
                    newPath = value.code === 'ar' ? currentPath.replace(/^\/en/, '/ar') : currentPath
                } else if (currentPath.startsWith('/ar')) {
                    newPath = value.code === 'en' ? currentPath.replace(/^\/ar/, '/en') : currentPath
                } else {
                    // If no locale in path, add it
                    newPath = `/${value.code}${currentPath}`
                }

                // Navigate to the new locale URL
                window.location.href = newPath + currentSearch + currentHash
            }
            onMenuClose(type)
        },
        [dispatch, onMenuClose]
    )

    const onModalOpen = useCallback((modal: 'signIn') => {
        setModals(prev => ({ ...prev, [modal]: true }))
    }, [])
    const onModalClose = useCallback((modal: 'signIn') => {
        setModals(prev => ({ ...prev, [modal]: false }))
    }, [])

    // Check for signin query param
    useEffect(() => {
        if (searchParams.get('signin') === 'true') {
            onModalOpen('signIn')

            // Remove the query param without refreshing the page
            const newSearchParams = new URLSearchParams(searchParams.toString())
            newSearchParams.delete('signin')
            const newUrl = `${pathname}${newSearchParams.toString() ? `?${newSearchParams.toString()}` : ''}`
            router.replace(newUrl)
        }
    }, [searchParams, pathname, router, onModalOpen])

    const handleSignInModalClose = useCallback(() => {
        onModalClose('signIn')
    }, [onModalClose])
    const handleUserMenuClose = useCallback(() => onMenuClose('user'), [onMenuClose])

    const displayUser = isMounted ? user : { signedIn: false }

    return (
        <>
            <Header
                navLinks={navLinks}
                options={options}
                selected={selected}
                menuAnchors={{
                    user: menuStates.user ? userMenuAnchorRef.current : null,
                    language: menuStates.language ? languageMenuAnchorRef.current : null,
                    currency: menuStates.currency ? currencyMenuAnchorRef.current : null
                }}
                onMenuOpen={onMenuOpen}
                onMenuClose={onMenuClose}
                onSelect={onSelect}
                modals={modals}
                onModalOpen={onModalOpen}
                onModalClose={onModalClose}
                t={t}
                tCurrency={tCurrency}
                user={displayUser}
                dispatch={dispatch}
                showLangCurrDropdown={showLangCurrDropdown}
                isOffersPage={isOffersPage}
                hideSignIn={shouldHideSignInOnItinerary}
                isResultsPage={isResultsPage}
            />
            <SignInModalWrapper
                open={modals.signIn || (isHomePage && !user.signedIn)}
                onClose={handleSignInModalClose}
                onMenuClose={handleUserMenuClose}
                disableClose={isHomePage && !user.signedIn}
            />
        </>
    )
}

export { HeaderContainer as Header }
export default HeaderContainer
